import React from 'react';
import SidebarLayout from '../../dashboard/components/SidebarLayout';
import {AppTitle} from '../../../Presentation/components/ui/Typography';
import Card from '../../../Presentation/components/ui/Card';
import Button from '../../../Presentation/components/ui/Button';
import Modal from '../../../Presentation/components/ui/Modal';
import {UserPlus} from 'lucide-react';

// Import existing components
import MarketersTable from '../components/MarketersTable';
import AddMarketerForm from '../components/AddMarketerForm';

// Import unified hook
import {useMarketersManagement} from '../hooks/useMarketersManagement';
import {PAGINATION_CONFIG} from '../../../constants/appConfig';

const MarketersManagementPage: React.FC = () => {
  // Unified marketers management
  const {
    // UI State
    search, setSearch,
    deletingMarketerId,
    isAddModalOpen, openAddModal, closeAddModal,

    // Data
    marketers, pagination,

    // Loading States
    loading,

    // Error States
    error,

    // Actions
    handleAddMarketerSuccess, handleDeleteMarketer,
    handlePageChange, handlePageSizeChange,
    updateMarketerProfile, updateMarketerPassword
  } = useMarketersManagement();

  // Filter marketers based on search
  const filteredMarketers = search.trim() === ''
    ? marketers || []
    : (marketers || []).filter(m => {
        const nameMatch = m.name?.toLowerCase().includes(search.toLowerCase());
        const phoneMatch = m.phone?.includes(search);
        return nameMatch || phoneMatch;
      });

  return (
    <SidebarLayout>
      <div className="p-6 space-y-6">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <AppTitle>المسوقين</AppTitle>
            <p className="text-gray-600 mt-1">إدارة المسوقين والمتابعة</p>
          </div>

          {/* Add Marketer Button */}
          <Button
            onClick={openAddModal}
            icon={<UserPlus className="w-4 h-4" />}
          >
            إضافة مسوق جديد
          </Button>
        </div>

        {/* Error Messages */}
        {error && (
          <Card className="p-4 bg-red-50 border border-red-200">
            <div className="text-red-700">{error}</div>
          </Card>
        )}

        {/* Search */}
        <Card className="p-6">
          <div className="flex gap-4 flex-wrap">
            <input
              className="flex-1 min-w-[200px] border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-primary/30 focus:border-brand-500"
              placeholder="بحث باسم المسوق أو رقم الجوال"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </Card>

        {/* Marketers Table */}
        <MarketersTable
          data={filteredMarketers}
          page={pagination?.page || 1}
          pageSize={pagination?.limit || PAGINATION_CONFIG.DEFAULT_PAGE_SIZE}
          totalCount={pagination?.total || 0}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onDeleteMarketer={handleDeleteMarketer}
          onUpdateProfile={updateMarketerProfile}
          onUpdatePassword={updateMarketerPassword}
          deletingMarketerId={deletingMarketerId}
          showPageSizeSelector={true}
          showPaginationInfo={true}
          loading={loading}
          exportable={true}
        />

        {/* Add Marketer Modal */}
        <Modal
          open={isAddModalOpen}
          onClose={closeAddModal}
          title="إضافة مسوق جديد"
          width="max-w-4xl"
        >
          <AddMarketerForm
            onSuccess={handleAddMarketerSuccess}
            loading={loading}
            onCancel={closeAddModal}
          />
        </Modal>
      </div>
    </SidebarLayout>
  );
};

export default MarketersManagementPage;
